<template>
    <div class="relative">
      <div v-if="loading" class="h-10rem">
        <div class="imab-logo-animated" style="left: 30%; top: 10%;">
          <div><span class="I">I</span><span class="M">M</span><span class="A">A</span><span class="B">B</span></div>
        </div>
      </div> 
      <div v-else>
        <DataTable :value="reactiveAttendance" aria-label="Roster Table">
          <Column field="employee_name" header="Employee" class="text-600 w-3 pl-3">
            <template #body="slotProps">
              <div class="flex items-center gap-2 pl-1">
                <Button
                  icon="pi pi-pencil"
                  @click="openChangeCompanyDialog(slotProps.data)"
                  :disabled="isSubmittingRoster || isDeletingAttendance || isDeletingBoth"
                  class="flex text-xs w-2rem surface-200 hover:surface-400 mr-1"
                />
                <div>
                  <p class="block font-bold pb-1 m-0 p-0">
                    {{ slotProps.data.employee_name }}
                  </p>
                  <p
                    :class="{
                      'font-bold text-cyan-300': slotProps.data.employee_company_id !== 1,
                      'text-xs text-300 m-0 p-0 pb-1': true
                    }"
                  >
                    {{ slotProps.data.employee_company_name }}
                  </p>
                  <p class="text-xs text-300 mr-2 m-0 p-0">
                    <span class="mr-2">@{{ slotProps.data.employee_id }}</span>
                    <span class="mr-2">{{ slotProps.data.employee_status }}</span>
                    <span class="mr-2">{{ slotProps.data.employee_department }}</span>
                  </p>
                </div>
              </div>
            </template>
          </Column>
          <Column field="shift_type" header="Type" class="text-600 w-1">
            <template #body="slotProps">
              <div class="items-center gap-2">
                <p class="block pb-1 m-0 p-0">
                  <Tag severity="info" :class="
                        {'bg-yellow-800': slotProps.data.shift_type==='LEAVE', 
                        'bg-cyan-800': slotProps.data.shift_type==='COVER', 
                        'surface-300': slotProps.data.shift_type==='SHIFT',
                        'bg-green-800': slotProps.data.shift_type==='ROSTER' }" style="letter-spacing: 2px;">
                        {{ slotProps.data.shift_type }}
                  </Tag>
                </p>
                <p class="block text-xs text-300 m-0 p-0">ID #{{ slotProps.data.event_id }}</p>
              </div>
            </template>
          </Column>
          <Column field="event_day" header="Day" class="text-600 w-1">
            <template #body="slotProps">
              <div class="items-center gap-2">
                <p class="block pb-1 m-0 p-0">{{ slotProps.data.event_day }}</p>
                <p class="block text-xs text-300 m-0 p-0">{{ slotProps.data.event_month }}</p>
              </div>
            </template>
          </Column>
          <Column field="event_date" header="Date" class="text-600 w-3">
            <template #body="slotProps">
              <NativeDateInput
                v-model="slotProps.data.event_date"
                :disabled="isSubmittingRoster || isDeletingAttendance || isDeletingBoth"
                @date-select="(e) => handleCalendarDateSelection(e, slotProps.data)"
                aria-label="Start Date"
              />
            </template>
          </Column>
          <Column field="event_start" header="Start" class="text-600 w-2">
            <template #body="slotProps">
                <div class="flex justify-content-start">
                    <div v-if="slotProps.data.event_type==='IN'" class="flex align-items-center justify-content-center mr-2 font-bold surface-100 w-5rem">IN</div>
                    <div v-if="slotProps.data.event_type==='OUT'" class="flex align-items-center justify-content-center mr-2 font-bold surface-100 w-5rem">OUT</div>
                    <div class="flex align-items-center justify-content-center">
                        <NativeTimeInput
                            :modelValue="slotProps.data.event_start"
                            :disabled="isSubmittingRoster || isDeletingAttendance || isDeletingBoth"
                            aria-label="Start Time"
                            :showSeconds="showSecondsForEvent(slotProps.data.event_type)"
                            @update:model-value="(newTime) => handleManualTimeEntry(newTime, slotProps.data)"/>
                    </div>
                </div>
            </template>
          </Column>
          <Column field="event_end" header="End" class="text-600 w-3 pr-2">
            <template #body="slotProps">
              <div v-if="slotProps.data.event_type==='ROSTER'" class="pr-2">
                <NativeTimeInput
                    :modelValue="slotProps.data.event_end"
                    :disabled="isSubmittingRoster || isDeletingAttendance || isDeletingBoth"
                    aria-label="End Time"
                    :showSeconds="showSecondsForEvent(slotProps.data.event_type)"
                    @update:model-value="(newTime) => handleManualTimeEntry(newTime, slotProps.data, 'end')"/>
              </div>
              <div v-else>
              <div v-if="slotProps.data.event_type==='IN'" class="flex align-items-center">
                <Tag severity="info" :class="
                    {'block bg-yellow-800 mb-1': attendanceInStatus==='LATE', 
                    'block bg-cyan-800 mb-1': attendanceInStatus==='EARLY', 
                    'block surface-300 mb-1': attendanceInStatus==='ONTIME',
                    'p-2 mr-1 w-full': true}">
                    {{ attendanceInStatus }}{{ getStatusDuration('IN') }}
                </Tag>
                <i v-if="getStatusDurationWarning('IN')" 
                   class="pi pi-exclamation-triangle text-yellow-500 ml-1"
                   v-tooltip.bottom="'Recheck the start time'">
                </i>
              </div>
              <div v-if="slotProps.data.event_type==='OUT'" class="flex align-items-center">
                <Tag severity="info" :class="
                    {'block bg-green-800': attendanceOutStatus==='OVERTIME', 
                    'block bg-red-800': attendanceOutStatus==='EARLY', 
                    'block surface-50 text-xs': attendanceOutStatus==='NOT', 
                    'block surface-300': attendanceOutStatus==='ONTIME',
                    'p-2 mr-1 w-full': true}">
                    {{ attendanceOutStatus }}{{ getStatusDuration('OUT') }}
                </Tag>
                <i v-if="getStatusDurationWarning('OUT')" 
                   class="pi pi-exclamation-triangle text-yellow-500 ml-1"
                   v-tooltip.bottom="'Recheck the end time'">
                </i>
              </div>
              </div>
            </template>
          </Column>
        </DataTable>
        <footer class="flex overflow-hidden mt-3">
          <div class="flex-shrink-0 flex align-items-center justify-content-left mr-2">
            <Button type="button" icon="pi pi-trash" @click="toggle" :loading="isDeletingAttendance" :disabled="isSubmittingRoster || isDeletingBoth || isDeletingAttendance" aria-haspopup="true" severity="danger" aria-controls="overlay_menu" />
            <Menu ref="menu" id="overlay_menu" :model="items" :popup="true" />
          </div>
          <div v-if="!hasOutEntry" class="flex-shrink-1 flex-grow-1 flex align-items-center justify-content-left mr-2">
            <Button
              label="Add Checkout"
              icon="pi pi-plus"
              class="bg-cyan-700 hover:bg-cyan-500"
              :disabled="isBusy"
              @click="addCheckoutRow"
            />
          </div>
          <div v-if="hasOvertimeApprovedEntry !== true && hasOutEntry && hasOvertimeEntry" class="flex-shrink-1 flex-grow-1 flex align-items-center justify-content-left mr-2">
            <Button label="Approve Overtime" icon="pi pi-check" @click="updateOvertimeStatus(true)" :loading="isApproving" :disabled="isSubmittingRoster || isDeletingBoth || isDeletingAttendance" type="submit" class="bg-green-700 hover:bg-green-400"/>
          </div>
          <div v-else-if="hasOvertimeApprovedEntry === true && hasOutEntry && hasOvertimeEntry" class="flex-shrink-1 flex-grow-1 flex align-items-center justify-content-left mr-2">
            <Button label="Reject Overtime" icon="pi pi-times" @click="updateOvertimeStatus(false)" :loading="isApproving" :disabled="isSubmittingRoster || isDeletingBoth || isDeletingAttendance" type="submit" class="surface-300 hover:surface-500"/>
          </div>
          <div v-else class="flex-shrink-1 flex-grow-1 flex align-items-center justify-content-left mr-2">
            <Button label="Approve Overtime" icon="pi pi-check" disabled  type="submit" class="surface-300 hover:surface-500"/>
          </div>
          <div v-if="hasInOutEntries && calculateTotalDurations" class="flex-shrink-1 flex-grow-1 flex align-items-center gap-2 justify-content-left mr-2">
            <div class="flex surface-100 align-items-center justify-content-center border-round p-2">
              <span class="text-xs font-light">Roster: <span class="font-bold">{{ calculateTotalDurations.shiftDuration }}</span></span>
            </div>
            <div class="flex surface-100 align-items-center justify-content-center border-round p-2">
              <span class="text-xs font-light">Worked: <span class="font-bold">{{ calculateTotalDurations.workedDuration }}</span></span>
            </div>
            <div class="flex surface-100 align-items-center justify-content-center border-round p-2"
                 :class="{'bg-green-900': calculateTotalDurations.netWorkedHours === calculateTotalDurations.workedDuration}">
              <span class="text-xs font-light">Net: <span class="font-bold">{{ calculateTotalDurations.netWorkedHours }}</span></span>
            </div>
          </div>
          <div class="flex-shrink-0 flex align-items-center justify-content-end mr-2">
            <Button label="Cancel" class="w-full surface-200 hover:surface-400" @click="handleBack" :disabled="isBusy" aria-label="Cancel and go back" />
          </div>
          <div class="flex-shrink-0 flex align-items-center justify-content-end">
            <Button label="Save Attendance" severity="primary" type="submit" @click="submitForm" :loading="isSubmittingRoster || checkoutSubmitting" :disabled="isBusy" class="w-full hover:bg-primary-300" aria-label="Edit selected rosters" />
          </div>
        </footer>
      </div>
    </div>
    <Dialog v-model:visible="changeCompany" :style="{ width: '20vw' }" :modal="true">
      <template #header>
        <h2 class="w-full m-0 p-0">Select Company</h2>
      </template>
      <Dropdown
        v-model="selectedCompany"
        :options="companiesList"
        filter
        optionLabel="company_name"
        placeholder="All Companies"
        class="w-full"
      >
        <template #value="slotProps">
          <div v-if="slotProps.value" class="flex align-items-center">
            {{ slotProps.value.company_name }}
          </div>
          <span v-else class="text-sm text-300">
            {{ slotProps.placeholder }}
          </span>
        </template>
        <template #option="slotProps">
          <div class="flex align-items-center">
            {{ slotProps.option.company_name }}
          </div>
        </template>
      </Dropdown>
      <footer class="flex overflow-hidden mt-3">
        <div class="flex-shrink-0 flex align-items-center justify-content-start mr-2">
          <Button
            label="Cancel"
            class="w-full surface-200 hover:surface-400"
            @click="cancelChangeCompany"
          />
        </div>
        <div class="flex-grow-1 flex-shrink-1 flex align-items-center justify-content-start">
          <Button
            label="Update Company"
            severity="primary"
            type="submit"
            @click="updateCompany"
          />
        </div>
      </footer>
    </Dialog>
</template>

<script setup>
import { onMounted, watch, defineProps, defineEmits, computed, ref } from 'vue';
import { useAttendanceSingle } from "@/composables/api/useAttendanceSingle";
import { useAttendanceSingleUpdate } from "@/composables/api/useAttendanceSingleUpdate";
import { useAttendanceSingleDelete } from "@/composables/api/useAttendanceSingleDelete";
import { useAttendanceSingleDeleteBoth } from "@/composables/api/useAttendanceSingleDeleteBoth";
import { useAttendanceSingleApprove } from '@/composables/api/useAttendanceSingleApprove';
import { useAttendanceMark } from '@/composables/api/useAttendanceMark';
import { useUtils } from '@/composables/useUtils';
import { useUserSelections } from '@/composables/useUserSelections';
import { useToast } from 'primevue/usetoast';
import Menu from 'primevue/menu';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import Button from 'primevue/button';
import Tooltip from 'primevue/tooltip';
import Dropdown from 'primevue/dropdown';
import Dialog from 'primevue/dialog';
import NativeDateInput from '@/components/NativeDateInput.vue';
import NativeTimeInput from '@/components/NativeTimeInput.vue';

const props = defineProps({
  selectedIds: {
    type: Object,
    required: true
  }
});

const emit = defineEmits(['updateSuccess', 'deleteSuccess', 'handleBack']);

const menu = ref();
const items = computed(() => {
  const baseItems = [
    {
      label: 'Delete Attendance',
      command: () => {
        deleteAttendance();
      }
    },
    {
      label: 'Delete Attendance and Roster',
      command: () => {
        deleteBoth();
      }
    }
  ];

  if (hasOutEntry.value) {
    baseItems.unshift({
      label: 'Delete Logout Entry',
      command: () => {
        deleteLogoutOnly();
      }
    });
  }

  return [
    {
      label: 'Options',
      items: baseItems
    }
  ];
});

const toggle = (event) => {
    menu.value.toggle(event);
};

const { 
  getExactTimestamp,
  updateDateInfo,
  compareTimestamps,
  parseUTCDate,
  generateBatchName,
  calculateDuration,
  calculateNetWorkedHours
} = useUtils();

const { singleRoster, loading, fetchSingleAttendance } = useAttendanceSingle();
const { isSubmittingRoster, submitBulkUpdate } = useAttendanceSingleUpdate();
const { isDeletingBoth, deleteBoth: deleteBothApi } = useAttendanceSingleDeleteBoth();
const { isDeleting: isDeletingAttendance, deleteAttendance: deleteAttendanceApi } = useAttendanceSingleDelete();
const { isApproving, updateOverBelowStatus } = useAttendanceSingleApprove();
const { submitting: checkoutSubmitting, addAttendanceCheckout } = useAttendanceMark();
const { companiesList } = useUserSelections();


const reactiveAttendance = ref([]);
const toast = useToast();

const event_shift_start = ref(null);
const event_start = ref(null);
const event_end = ref(null);
const event_shift_end = ref(null);

const changeCompany = ref(false);
const selectedCompany = ref(null);
const editingEmployeeId = ref(null);
const pendingCheckout = ref(false);

const isBusy = computed(() => (
  isSubmittingRoster.value
  || checkoutSubmitting.value
  || isDeletingAttendance.value
  || isDeletingBoth.value
));

// Computed properties
const showSecondsForEvent = (eventType) => {
  return eventType !== 'ROSTER';
};

const attendanceInStatus = computed(() => {
  return compareTimestamps(event_start.value, event_shift_start.value, 'IN');
});

const attendanceOutStatus = computed(() => {
  return compareTimestamps(event_end.value, event_shift_end.value, 'OUT');
});

const hasOutEntry = computed(() => {
  return reactiveAttendance.value.some(event => event.event_type === 'OUT');
});

const hasOvertimeEntry = computed(() => {
  return reactiveAttendance.value.some(event => event.attendance_status === 'OVERTIME' || event.attendance_status === 'EARLY');
});

const hasOvertimeApprovedEntry = computed(() => {
  return reactiveAttendance.value.some(event => event.over_below_status === true);
});

const hasInOutEntries = computed(() => {
  return reactiveAttendance.value.some(event => event.event_type === 'IN') &&
         reactiveAttendance.value.some(event => event.event_type === 'OUT');
});

const calculateTotalDurations = computed(() => {
  if (!hasInOutEntries.value) return null;

  const rosterEvent = reactiveAttendance.value.find(event => event.event_type === 'ROSTER');
  const inEvent = reactiveAttendance.value.find(event => event.event_type === 'IN');
  const outEvent = reactiveAttendance.value.find(event => event.event_type === 'OUT');

  if (!rosterEvent || !inEvent || !outEvent) return null;

  const shiftDuration = calculateDuration(
    getExactTimestamp(rosterEvent.event_start),
    getExactTimestamp(rosterEvent.event_end)
  );

  const workedDuration = calculateDuration(
    getExactTimestamp(inEvent.event_start),
    getExactTimestamp(outEvent.event_start)
  );

  const netWorkedHours = calculateNetWorkedHours(
    getExactTimestamp(rosterEvent.event_start),
    getExactTimestamp(rosterEvent.event_end),
    getExactTimestamp(inEvent.event_start),
    getExactTimestamp(outEvent.event_start),
    compareTimestamps(inEvent.event_start, rosterEvent.event_start, 'IN'),
    compareTimestamps(outEvent.event_start, rosterEvent.event_end, 'OUT'),
    outEvent.over_below_status
  );

  return {
    shiftDuration,
    workedDuration,
    netWorkedHours
  };
});

const formatDuration = (minutes) => {
  if (minutes < 60) {
    return `${Math.round(minutes)}m`;
  } else {
    return `${(minutes / 60).toFixed(1)}h`;
  }
};

const getStatusDuration = computed(() => (type) => {
  if (type === 'IN') {
    const inTime = new Date(event_start.value);
    const shiftStart = new Date(event_shift_start.value);
    const diffMinutes = Math.abs(inTime - shiftStart) / (1000 * 60);
    
    if (attendanceInStatus.value === 'ONTIME') return '';
    return ` (${formatDuration(diffMinutes)})`;
  } else if (type === 'OUT') {
    const outTime = new Date(event_end.value);
    const shiftEnd = new Date(event_shift_end.value);
    const diffMinutes = Math.abs(outTime - shiftEnd) / (1000 * 60);
    
    if (attendanceOutStatus.value === 'ONTIME' || attendanceOutStatus.value === 'NOT') return '';
    return ` (${formatDuration(diffMinutes)})`;
  }
  return '';
});

const getStatusDurationWarning = computed(() => (type) => {
  if (type === 'IN') {
    const inTime = new Date(event_start.value);
    const shiftStart = new Date(event_shift_start.value);
    const diffMinutes = Math.abs(inTime - shiftStart) / (1000 * 60);
    return diffMinutes >= 120;
  } else if (type === 'OUT') {
    const outTime = new Date(event_end.value);
    const shiftEnd = new Date(event_shift_end.value);
    const diffMinutes = Math.abs(outTime - shiftEnd) / (1000 * 60);
    return diffMinutes >= 120;
  }
  return false;
});

const handleManualTimeEntry = (newTime, roster, type = 'start') => {
  // Get the original date from event_date
  const originalDate = new Date(roster.event_date);
  
  // Extract time components from the new time
  const hours = newTime.getHours();
  const minutes = newTime.getMinutes();
  const seconds = newTime.getSeconds();

  // Create a new date using the original date but with new time
  const updatedDate = new Date(originalDate);
  updatedDate.setHours(hours, minutes, seconds);
  
  // Update either start or end time based on type
  if (type === 'end') {
    // If end time is earlier than start time, add one day
    const startTime = new Date(roster.event_start);
    if (updatedDate < startTime) {
      updatedDate.setDate(updatedDate.getDate() + 1);
    }
    roster.event_end = updatedDate;
  } else {
    roster.event_start = updatedDate;
  }
};

const openChangeCompanyDialog = (rowData) => {
  editingEmployeeId.value = rowData.employee_id;
  const companyId = rowData.employee_company_id != null
    ? Number(rowData.employee_company_id)
    : Number(props.selectedIds?.employee_company_id);
  const list = companiesList.value ?? [];
  selectedCompany.value = (companyId != null && !Number.isNaN(companyId))
    ? list.find((c) => Number(c.company_id) === companyId) ?? null
    : null;
  changeCompany.value = true;
};

const updateCompany = () => {
  if (editingEmployeeId.value && selectedCompany.value) {
    reactiveAttendance.value = reactiveAttendance.value.map((event) => {
      if (event.employee_id === editingEmployeeId.value) {
        return {
          ...event,
          employee_company_id: selectedCompany.value.company_id,
          employee_company_name: selectedCompany.value.company_name,
        };
      }
      return event;
    });

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Company updated successfully',
      life: 3000,
      group: 'br',
    });
  }

  changeCompany.value = false;
  editingEmployeeId.value = null;
  selectedCompany.value = null;
};

const cancelChangeCompany = () => {
  changeCompany.value = false;
  editingEmployeeId.value = null;
  selectedCompany.value = null;
};

const handleCalendarDateSelection = (newDate, roster) => {
  const currentDate = new Date(roster.event_start);
  currentDate.setFullYear(newDate.getFullYear(), newDate.getMonth(), newDate.getDate());
  roster.event_start = currentDate;
  roster.event_end = new Date(roster.event_end);
  roster.event_end.setFullYear(newDate.getFullYear(), newDate.getMonth(), newDate.getDate());
  updateDateInfo(roster);
};

const updateIndividualTimestamps = () => {
  const rosterEvent = reactiveAttendance.value.find(event => event.event_type === 'ROSTER');
  const inEvent = reactiveAttendance.value.find(event => event.event_type === 'IN');
  const outEvent = reactiveAttendance.value.find(event => event.event_type === 'OUT');

  event_shift_start.value = rosterEvent?.event_start ?? null;
  event_shift_end.value = rosterEvent?.event_end ?? null;
  event_start.value = inEvent?.event_start ?? null;
  event_end.value = outEvent?.event_start ?? null;
};

const addCheckoutRow = () => {
  const rosterEvent = reactiveAttendance.value.find(event => event.event_type === 'ROSTER');
  const inEvent = reactiveAttendance.value.find(event => event.event_type === 'IN');
  if (!rosterEvent || !inEvent || hasOutEntry.value) return;

  const checkoutTime = new Date(rosterEvent.event_end);
  reactiveAttendance.value.push({
    ...inEvent,
    attendance_status: compareTimestamps(checkoutTime, rosterEvent.event_end, 'OUT'),
    event_id: null,
    event_type: 'OUT',
    event_start: checkoutTime,
    event_end: checkoutTime,
    event_date: new Date(checkoutTime),
    event_month: checkoutTime.toLocaleString('default', { month: 'long', timeZone: 'UTC' }),
    event_day: checkoutTime.toLocaleString('default', { weekday: 'long', timeZone: 'UTC' }),
    over_below_status: false
  });
  pendingCheckout.value = true;
  updateIndividualTimestamps();
};

const loadRosterData = async () => {
  try {
    await fetchSingleAttendance(props.selectedIds.employee_id, props.selectedIds.roster_id, props.selectedIds.attendance_id);
    if (singleRoster.value) {
      // Get the roster row first
      const rosterRow = singleRoster.value.find(row => row.event_type === 'ROSTER');
      // Generate batch_name from roster's event_start
      const batchName = rosterRow ? generateBatchName(new Date(rosterRow.event_start)) : generateBatchName();
      // Company from API or from parent selected row so change-company dropdown can show current value
      const companyId = rosterRow?.employee_company_id ?? props.selectedIds?.employee_company_id;
      const companyName = rosterRow?.employee_company_name ?? props.selectedIds?.employee_company_name;

      // Map the data and add batch_name and company to all rows
      reactiveAttendance.value = singleRoster.value.map(roster => {
        const startDate = parseUTCDate(roster.event_start);
        const endDate = parseUTCDate(roster.event_end);
        
        return {
          ...roster,
          event_start: startDate,
          event_end: endDate,
          event_date: startDate,
          event_month: startDate.toLocaleString('default', { month: 'long', timeZone: 'UTC' }),
          event_day: startDate.toLocaleString('default', { weekday: 'long', timeZone: 'UTC' }),
          batch_name: batchName,
          employee_company_id: roster.employee_company_id ?? companyId,
          employee_company_name: roster.employee_company_name ?? companyName
        };
      });
    }
    setupWatchers();
    updateIndividualTimestamps();
  } catch (error) {
    console.error('Error fetching roster data:', error);
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to load roster data', life: 12000, group: 'br'});
  }
};

// Submit Attendance Form
const submitForm = async () => {
  const rosterPayload = { rows: [] };
  const inPayload = { rows: [] };
  const outPayload = { rows: [] };
  let newCheckoutTimestamp = null;

  reactiveAttendance.value.forEach(event => {
    switch(event.event_type) {
      case 'ROSTER':
        rosterPayload.rows.push({
          rosterId: event.event_id,
          employeeId: event.employee_id,
          employee_company_id: event.employee_company_id,
          rosterDay: event.event_day,
          rosterMonth: event.event_month,
          rosterStart: getExactTimestamp(event.event_start),
          rosterEnd: getExactTimestamp(event.event_end),
          batchName: event.batch_name,  // Include batch_name in the payload
        });
        break;
      case 'IN':
        inPayload.rows.push({
          attendanceId: event.event_id,
          employeeId: event.employee_id,
          companyId: event.employee_company_id,
          attendanceInOut: getExactTimestamp(event.event_start),
          attendanceStatus: attendanceInStatus.value,
          batchName: event.batch_name,  // Include batch_name in the payload
        });
        break;
      case 'OUT':
        if (event.event_id) {
          outPayload.rows.push({
            attendanceId: event.event_id,
            employeeId: event.employee_id,
            companyId: event.employee_company_id,
            attendanceInOut: getExactTimestamp(event.event_start),
            attendanceStatus: attendanceOutStatus.value,
            batchName: event.batch_name,  // Include batch_name in the payload
          });
        } else {
          newCheckoutTimestamp = getExactTimestamp(event.event_start);
        }
        break;
    }
  });

  if (pendingCheckout.value && newCheckoutTimestamp) {
    const inEvent = reactiveAttendance.value.find(event => event.event_type === 'IN');
    const checkinTimestamp = inEvent ? getExactTimestamp(inEvent.event_start) : null;
    if (!checkinTimestamp || newCheckoutTimestamp < checkinTimestamp) {
      toast.add({
        severity: 'warn',
        summary: 'Invalid checkout',
        detail: 'Checkout cannot be earlier than check-in.',
        life: 6000,
        group: 'br'
      });
      return;
    }
  }

  const result = await submitBulkUpdate(rosterPayload, inPayload, outPayload);

  if (result.success && pendingCheckout.value && newCheckoutTimestamp) {
    const checkoutResult = await addAttendanceCheckout({
      attendance_id: props.selectedIds.attendance_id,
      checkout_timestamp: newCheckoutTimestamp
    });
    if (!checkoutResult.success) {
      toast.add({
        severity: 'error',
        summary: 'Checkout not added',
        detail: checkoutResult.error,
        life: 12000,
        group: 'br'
      });
      return;
    }
    result.messages.push(checkoutResult.data.message);
    pendingCheckout.value = false;
  }

  if (result.success) {
    toast.add({ 
      severity: result.severity, 
      summary: result.summary, 
      detail: result.messages.join('. '), 
      life: 4000,
      group: 'br'
    });
    emit('updateSuccess');
  } else {
    toast.add({ 
      severity: result.severity, 
      summary: result.summary, 
      detail: result.error, 
      life: 12000,
      group: 'br'
    });
  }
};

// Delete Attendance and Roster
const deleteBoth = async () => {
  const rosterPayload = { rosterIds: [], employeeIds: [] };
  const attendancePayload = [];

  reactiveAttendance.value.forEach(event => {
    if (event.event_type === 'ROSTER') {
      rosterPayload.rosterIds.push(event.event_id);
      rosterPayload.employeeIds.push(event.employee_id);
    } else if (event.event_type === 'IN') {
      if (!attendancePayload.some(item => item.attendanceId === event.event_id)) {
        attendancePayload.push({ attendanceId: event.event_id, employeeId: event.employee_id });
      }
    }
  });

  const result = await deleteBothApi(rosterPayload, attendancePayload);

  if (result.success) {
    toast.add({
      severity: result.severity,
      summary: result.summary,
      detail: result.message,
      life: 4000,
      group: 'br'
    });
    emit('updateSuccess');
  } else {
    toast.add({
      severity: result.severity,
      summary: result.summary,
      detail: result.error,
      life: 4000,
      group: 'br'
    });
  }
};

// Delete Attendance Only
const deleteAttendance = async () => {
  const attendancePayload = [];

  reactiveAttendance.value.forEach(event => {
    if (event.event_type === 'IN') {
      if (!attendancePayload.some(item => item.attendanceId === event.event_id)) {
        attendancePayload.push({ attendanceId: event.event_id, employeeId: event.employee_id });
      }
    }
  });

  const result = await deleteAttendanceApi(attendancePayload);

  toast.add({
    severity: result.severity,
    summary: result.summary,
    detail: result.success ? result.message : result.error,
    life: 4000,
    group: 'br'
  });

  if (result.success) {
    emit('updateSuccess');
  }
};

// Delete Attendance Logout Entry
const deleteLogoutOnly = async () => {
  const attendancePayload = [];

  reactiveAttendance.value.forEach(event => {
    if (event.event_type === 'OUT') {
      if (!attendancePayload.some(item => item.attendanceId === event.event_id)) {
        attendancePayload.push({ attendanceId: event.event_id, employeeId: event.employee_id });
      }
    }
  });

  const result = await deleteAttendanceApi(attendancePayload);

  toast.add({
    severity: result.severity,
    summary: result.summary,
    detail: result.success ? result.message : result.error,
    life: 4000,
    group: 'br'
  });

  if (result.success) {
    emit('updateSuccess');
  }
};

// Delete Attendance Only
const updateOvertimeStatus = async (status) => {
  const attendancePayload = reactiveAttendance.value
    .filter(event => event.event_type === 'IN')
    .map(event => ({
      overBelowStatus: status,
      attendanceId: event.event_id,
      employeeId: event.employee_id
    }));

  const result = await updateOverBelowStatus(attendancePayload);

  toast.add({
    severity: result.severity,
    summary: result.summary,
    detail: result.success ? result.message : result.error,
    life: 4000,
    group: 'br'
  });

  if (result.success) {
    emit('updateSuccess');
  }
};

const setupWatchers = () => {
  reactiveAttendance.value.forEach((roster, index) => {
    watch(() => reactiveAttendance.value[index], updateIndividualTimestamps, { deep: true });
  });
};

watch(reactiveAttendance, (newAttendance) => {
  const rosterRow = newAttendance.find(row => row.event_type === 'ROSTER');
  if (rosterRow) {
    const batchName = generateBatchName(rosterRow.event_date);
    // Update batch_name for all rows
    newAttendance.forEach(row => {
      row.batch_name = batchName;
    });
  }
  updateIndividualTimestamps();
}, { deep: true });

const handleBack = async () => {
  emit('handleBack');
};

const vTooltip = Tooltip;

onMounted(loadRosterData);
watch(() => props.selectedIds, loadRosterData);
</script>
